/**
 * PDF compression using pdfjs-dist (render pages to canvas) and jspdf (rebuild PDF).
 * Compresses PDFs while keeping fonts readable and images properly visible.
 */
/* eslint-disable no-console */

import { jsPDF } from 'jspdf';


import * as pdfjs from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL as string}/pdf.worker.min.js`;

type PDFDocumentProxy = pdfjs.PDFDocumentProxy

/** MIME type for PDF */
export const PDF_MIME_TYPE = 'application/pdf';

/** True if file is a PDF by type or extension (handles empty MIME from some OS/browsers) */
export function isPdfFile(file: File): boolean {
  if (!file?.name) return false;
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === PDF_MIME_TYPE ||
    name.endsWith('.pdf') ||
    type === 'application/x-pdf'
  );
}

/** Browser canvas size limit (conservative to avoid "canvas size exceeded" in local) */
const MAX_CANVAS_DIM = 4096;
const RENDER_SCALE = 1.4;
const JPEG_QUALITY = 0.60;

/**
 * Renders a single PDF page to a canvas and returns it as a JPEG data URL.
 * Caps scale so canvas stays under MAX_CANVAS_DIM to avoid browser errors on large pages.
 */
async function renderPageToImageDataUrl(
  pdf: PDFDocumentProxy,
  pageNumber: number
): Promise<{ dataUrl: string; width: number; height: number }> {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale: RENDER_SCALE });
  let scale = RENDER_SCALE;
  if (
    viewport.width > MAX_CANVAS_DIM ||
    viewport.height > MAX_CANVAS_DIM
  ) {
    scale =
      RENDER_SCALE *
      Math.min(
        MAX_CANVAS_DIM / viewport.width,
        MAX_CANVAS_DIM / viewport.height
      );
  }
  const scaledViewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = scaledViewport.width;
  canvas.height = scaledViewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas 2d context');

  await page.render({
    canvasContext: ctx,
    viewport: scaledViewport,
  }).promise;

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return {
    dataUrl,
    width: scaledViewport.width,
    height: scaledViewport.height,
  };
}

/**
 * Compresses a PDF file by rasterizing each page to an image and rebuilding with jsPDF.
 * Uses RENDER_SCALE and JPEG_QUALITY constants to balance size reduction and readability.
 *
 * @param file - Original PDF File
 * @returns Smaller File on success, original File if compression didn't help, or null on error
 */
export async function compressPdfFile(
  file: File,
): Promise<File | null> {
  console.log('[PDF compress] Start:', { name: file.name, size: file.size });

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.warn('[PDF compress] Skipped (not in browser)');
    return null;
  }

  try {

    // Load the PDF document with current worker setting.
    const loadPdf = async (data: ArrayBuffer): Promise<PDFDocumentProxy> => {
      console.log('[PDF compress] Loading PDF document...');
      return pdfjs.getDocument({
        data,
        useSystemFonts: true,
        disableFontFace: false,
      }).promise;
    };

    // Attempt to load with proper worker.
    const arrayBuffer = await file.arrayBuffer();
    console.log('[PDF compress] File arrayBuffer obtained, size:', arrayBuffer.byteLength);


	const pdf = await loadPdf(arrayBuffer);
    console.log('[PDF compress] Document loaded (same-origin worker)');
    
    const numPages = pdf.numPages;
    console.log('[PDF compress] Pages:', numPages);

    if (numPages === 0) {
      console.warn('[PDF compress] No pages');
      return null;
    }

    // First page dimensions (cap scale to browser limit).
    const firstPage = await pdf.getPage(1);
    const firstViewportRaw = firstPage.getViewport({ scale: RENDER_SCALE });
    let firstScale = RENDER_SCALE;
    if (firstViewportRaw.width > MAX_CANVAS_DIM || firstViewportRaw.height > MAX_CANVAS_DIM) {
      firstScale = RENDER_SCALE * Math.min(
        MAX_CANVAS_DIM / firstViewportRaw.width,
        MAX_CANVAS_DIM / firstViewportRaw.height
      );
    }
    const firstViewport = firstPage.getViewport({ scale: firstScale });
    const pageWidth = firstViewport.width;
    const pageHeight = firstViewport.height;
    console.log('[PDF compress] Page size (pt):', { pageWidth, pageHeight });

    const doc = new jsPDF({
      orientation: pageWidth > pageHeight ? 'l' : 'p',
      unit: 'pt',
      format: [pageWidth, pageHeight],
      compress: true,
    });

    for (let i = 1; i <= numPages; i++) {
      console.log(`[PDF compress] Rendering page ${i}...`);
      const { dataUrl, width, height } = await renderPageToImageDataUrl(pdf, i);
      if (i > 1) doc.addPage([pageWidth, pageHeight]);
      doc.addImage(dataUrl, 'JPEG', 0, 0, width, height, undefined, 'FAST');
      if (i === 1 || i === numPages || i % 10 === 0) {
        console.log('[PDF compress] Rendered page', i, 'of', numPages);
      }
    }

    const blob = doc.output('blob');
    const compressed = new File([blob], file.name, { type: PDF_MIME_TYPE });
    const saved = file.size - compressed.size;
    console.log('[PDF compress] Done:', {
      originalSize: file.size,
      compressedSize: compressed.size,
      savedBytes: saved,
      savedPercent: ((saved / file.size) * 100).toFixed(1) + '%',
    });

    // Return original if compression made it larger (can happen on already-small/optimized PDFs)
    if (compressed.size >= file.size) {
      console.log('[PDF compress] Compressed file is not smaller, returning original');
      return file;
    }
    return compressed;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[PDF compress] Failed:', { message: msg, err });
    return null;
  }
}